import { useState, useMemo } from "react";
import Spinner from "../../../globalComponents/ui/Spinner";
import { copyMappings } from "./kpiAssetApi";

/** Copy-from-FY modal: copy KPI asset mappings from another financial year. */
export default function CopyFromFYModal({ fyData, allQuestions, targetFy, onClose, onCopied }) {
  /** Default source = next-lower FY id; falls back to any other FY. */
  const defaultSourceFy = useMemo(() => {
    const others = (fyData || []).filter(f => f.id !== targetFy?.id);
    const lower = others
      .filter(f => f.id < (targetFy?.id ?? Infinity))
      .sort((a, b) => b.id - a.id)[0];
    return lower || others[0] || null;
  }, [fyData, targetFy]);

  const [sourceFyId, setSourceFyId] = useState(defaultSourceFy?.id ?? "");
  const [scope, setScope] = useState("all");
  const [selectedKpiIds, setSelectedKpiIds] = useState(() => new Set());
  const [overwrite, setOverwrite] = useState(false);
  const [search, setSearch] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const filteredKpis = useMemo(() => {
    const s = search.trim().toLowerCase();
    if (!s) return allQuestions;
    return allQuestions.filter(q =>
      (q.title || "").toLowerCase().includes(s) ||
      (q.moduleName || "").toLowerCase().includes(s)
    );
  }, [allQuestions, search]);

  const toggleKpi = (id) => setSelectedKpiIds(prev => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const filteredAllChecked = filteredKpis.length > 0
    && filteredKpis.every(q => selectedKpiIds.has(q.questionId));
  const toggleAllVisible = () => setSelectedKpiIds(prev => {
    const next = new Set(prev);
    for (const q of filteredKpis) {
      if (filteredAllChecked) next.delete(q.questionId); else next.add(q.questionId);
    }
    return next;
  });

  const canSubmit = !!sourceFyId
    && !!targetFy?.id
    && (scope === "all" || selectedKpiIds.size > 0)
    && !submitting;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setError("");
    setSubmitting(true);
    try {
      const body = {
        sourceFyid: Number(sourceFyId),
        targetFyid: Number(targetFy.id),
        overwrite,
      };
      if (scope === "selected") {
        body.kpiIds = Array.from(selectedKpiIds).map(Number);
      }
      const res = await copyMappings(body);
      const data = res?.data;
      if (res?.isSuccess !== false && data?.success !== false) {
        onCopied({
          copied: data?.copied ?? 0,
          skipped: data?.skipped ?? 0,
          overwritten: data?.overwritten ?? 0,
        });
      } else {
        setError(data?.message || "Copy failed — please try again.");
      }
    } catch (e) {
      setError(e?.response?.data?.message || "Copy failed — please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const sourceOptions = (fyData || [])
    .filter(f => f.id !== targetFy?.id)
    .map(f => ({ value: f.id, label: f.financial_year_value }));

  return (
    <div className="kpa-modal-overlay">
      <div className="kpa-modal" role="dialog" aria-modal="true" aria-labelledby="kpa-copy-modal-title">

        <div style={{ padding: "16px 20px 14px", borderBottom: "1px solid var(--kpa-slate-100)", display: "flex", alignItems: "flex-start", gap: 10 }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div id="kpa-copy-modal-title" style={{ fontSize: 14, fontWeight: 700, color: "var(--kpa-slate-900)", marginBottom: 2 }}>
              <i className="fas fa-copy" style={{ marginRight: 6, color: "var(--kpa-primary)" }} aria-hidden="true" />
              Copy KPI Asset Mappings
            </div>
            <div style={{ fontSize: 11, color: "var(--kpa-slate-400)" }}>
              Copy into <strong style={{ color: "var(--kpa-slate-600)" }}>{targetFy?.financial_year_value}</strong>
            </div>
          </div>
          <button type="button" className="kpa-modal-close" onClick={onClose} aria-label="Close dialog">×</button>
        </div>

        <div style={{ overflowY: "auto", padding: "16px 20px", flex: 1, display: "flex", flexDirection: "column", gap: 16 }}>

          <div>
            <label className="kpa-label" htmlFor="kpa-copy-source-fy">Source Financial Year *</label>
            <select
              id="kpa-copy-source-fy"
              className={`kpa-select${sourceFyId ? " kpa-select--filled" : ""}`}
              value={sourceFyId ?? ""}
              onChange={e => setSourceFyId(e.target.value ? Number(e.target.value) : "")}
            >
              <option value="">Select source FY…</option>
              {sourceOptions.map(o => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>

          <fieldset style={{ border: "none", padding: 0, margin: 0 }}>
            <legend className="kpa-label">Scope</legend>
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              {[
                { value: "all", title: "All KPIs", help: "Copy every asset mapping from the source FY." },
                { value: "selected", title: "Selected KPIs only", help: "Pick specific KPIs to copy mappings for." },
              ].map(opt => {
                const active = scope === opt.value;
                return (
                  <label
                    key={opt.value}
                    style={{
                      display: "flex", alignItems: "flex-start", gap: 8, padding: "9px 12px",
                      border: `1.5px solid ${active ? "var(--kpa-primary)" : "var(--kpa-slate-200)"}`,
                      background: active ? "var(--kpa-blue-50)" : "#fff",
                      borderRadius: 8, cursor: "pointer", userSelect: "none",
                    }}
                  >
                    <input
                      type="radio"
                      name="kpa-copy-scope"
                      value={opt.value}
                      checked={active}
                      onChange={() => setScope(opt.value)}
                      className="kpa-checkbox"
                      style={{ marginTop: 2 }}
                    />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 12.5, fontWeight: 600, color: active ? "var(--kpa-blue-700)" : "var(--kpa-slate-800)" }}>{opt.title}</div>
                      <div style={{ fontSize: 11, color: "var(--kpa-slate-500)" }}>{opt.help}</div>
                    </div>
                  </label>
                );
              })}
            </div>
          </fieldset>

          {scope === "selected" && (
            <div style={{ border: "1px solid var(--kpa-slate-200)", borderRadius: 10, overflow: "hidden" }}>
              <div style={{ padding: "8px 12px", background: "var(--kpa-slate-50)", borderBottom: "1px solid var(--kpa-slate-100)", display: "flex", alignItems: "center", gap: 8 }}>
                <i className="fas fa-search" style={{ fontSize: 11, color: "var(--kpa-slate-400)" }} aria-hidden="true" />
                <input
                  className="kpa-input"
                  style={{ padding: "5px 8px", borderRadius: 6 }}
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  placeholder="Search KPIs…"
                  aria-label="Search KPIs"
                />
                <span style={{ fontSize: 11, color: "var(--kpa-slate-500)", whiteSpace: "nowrap" }}>
                  {selectedKpiIds.size} of {allQuestions.length} selected
                </span>
              </div>
              {filteredKpis.length > 0 && (
                <label style={{ padding: "7px 12px", borderBottom: "1px solid var(--kpa-slate-100)", display: "flex", alignItems: "center", gap: 8, cursor: "pointer", fontSize: 11.5, fontWeight: 600, color: "var(--kpa-slate-600)", background: "var(--kpa-slate-75)" }}>
                  <input
                    type="checkbox"
                    className="kpa-checkbox"
                    checked={filteredAllChecked}
                    onChange={toggleAllVisible}
                  />
                  Select all{search.trim() ? " visible" : ""} ({filteredKpis.length})
                </label>
              )}
              <div style={{ maxHeight: 220, overflowY: "auto" }}>
                {filteredKpis.length === 0 ? (
                  <div style={{ padding: 16, textAlign: "center", fontSize: 11.5, color: "var(--kpa-slate-400)", fontStyle: "italic" }}>
                    No KPIs match this search.
                  </div>
                ) : filteredKpis.map(q => {
                  const checked = selectedKpiIds.has(q.questionId);
                  return (
                    <label
                      key={q.questionId}
                      style={{ display: "flex", alignItems: "center", gap: 8, padding: "7px 12px", borderBottom: "1px solid var(--kpa-slate-50)", cursor: "pointer", background: checked ? "var(--kpa-blue-50)" : "#fff" }}
                    >
                      <input
                        type="checkbox"
                        className="kpa-checkbox"
                        checked={checked}
                        onChange={() => toggleKpi(q.questionId)}
                      />
                      <span style={{ flex: 1, fontSize: 12, color: "var(--kpa-slate-800)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={q.title}>
                        {q.title || "—"}
                      </span>
                    </label>
                  );
                })}
              </div>
            </div>
          )}

          <label style={{
            display: "flex", alignItems: "flex-start", gap: 8, padding: "10px 12px",
            border: `1.5px solid ${overwrite ? "var(--kpa-amber-300)" : "var(--kpa-slate-200)"}`,
            background: overwrite ? "var(--kpa-amber-50)" : "#fff",
            borderRadius: 8, cursor: "pointer", userSelect: "none",
          }}>
            <input
              type="checkbox"
              className="kpa-checkbox"
              checked={overwrite}
              onChange={e => setOverwrite(e.target.checked)}
              style={{ marginTop: 2, accentColor: "var(--kpa-amber-600)" }}
            />
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 12.5, fontWeight: 600, color: overwrite ? "var(--kpa-amber-800)" : "var(--kpa-slate-800)" }}>
                Replace existing mappings in target FY
              </div>
              <div style={{ fontSize: 11, color: "var(--kpa-slate-500)" }}>
                {overwrite
                  ? "Existing mappings with the same asset will have their units refreshed from the source FY."
                  : "Existing mappings in the target FY will be left untouched."}
              </div>
            </div>
          </label>

          {error && (
            <div className="kpa-error-box" role="alert" style={{ marginTop: 0, padding: "10px 14px", fontSize: 12 }}>
              <i className="fas fa-exclamation-circle" aria-hidden="true" />
              <span>{error}</span>
            </div>
          )}
        </div>

        <div style={{ padding: "12px 20px", borderTop: "1px solid var(--kpa-slate-100)", display: "flex", justifyContent: "flex-end", gap: 10 }}>
          <button type="button" className="kpa-btn kpa-btn--ghost" onClick={onClose} disabled={submitting}>
            Cancel
          </button>
          <button type="button" className="kpa-btn kpa-btn--primary" onClick={handleSubmit} disabled={!canSubmit}>
            {submitting
              ? <><Spinner size={11} color="#fff" style={{ marginRight: 6 }} />Copying…</>
              : <><i className="fas fa-copy" style={{ marginRight: 5 }} aria-hidden="true" />Copy</>}
          </button>
        </div>
      </div>
    </div>
  );
}
